import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AccessibleModal } from '~/components/account/AccessibleModal';
import {
  apiPreviewPlanSwitch,
  apiStartPlanCheckout,
  apiSwitchPlan,
  type AccountPlanOption,
  type PaidAccountPlanTier,
  type PlanApp,
  type PlanInterval,
} from '~/lib/api/plans';
import { formatCredits, formatMoney, planPrice } from '~/lib/plans-model';

/** The server's message when it has one (e.g. "Your Dunea account already has a plan…"), else the fallback. */
export function serverMessage(err: unknown, fallback: string): string {
  console.error(err);

  const message = (err as { response?: { data?: { message?: unknown } } })?.response?.data?.message;

  if (Array.isArray(message)) {
    return message.join(', ');
  }

  return typeof message === 'string' && message ? message : fallback;
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-2">
      <span className="text-[12px] text-muted">{label}</span>
      <span className="text-right text-[12.5px] text-primary">{value}</span>
    </div>
  );
}

function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      className="mt-3 flex items-start gap-2 rounded-lg border border-danger/30 bg-danger/[0.07] px-3 py-2.5 text-[12.5px] leading-normal text-danger"
    >
      <span className="i-ph:warning-circle mt-px flex-shrink-0 text-[14px]" />
      <span>{children}</span>
    </div>
  );
}

function ModalHeader({ icon, title, subtitle }: { icon: string; title: string; subtitle: string }) {
  return (
    <div className="mb-4 flex items-start gap-3.5">
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] border border-accent/20 bg-accent/10">
        <span className={`${icon} text-[17px] text-accent`} />
      </div>
      <div className="min-w-0 flex-1 pt-1">
        <h3 className="ui-serif m-0 mb-1 text-[20px] tracking-[-0.01em] text-primary">{title}</h3>
        <p className="m-0 text-[12px] leading-normal text-muted">{subtitle}</p>
      </div>
    </div>
  );
}

/**
 * Review a Plus/Pro plan, then hand off to Dodo's hosted checkout. Every figure
 * is the server's (price at this cadence, monthly credits). If the server
 * refuses — a website plan still running, a plan already active — its message
 * is shown as-is and nothing is charged.
 */
export function PlanCheckoutModal({
  plan,
  interval,
  savingsPercent,
  onClose,
}: {
  plan: AccountPlanOption;
  interval: PlanInterval;
  savingsPercent: number | null;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const price = planPrice(plan, interval);
  const isAnnual = interval === 'year';

  async function continueToPayment() {
    setBusy(true);
    setError(null);

    try {
      const { url } = await apiStartPlanCheckout(plan.tier as PaidAccountPlanTier, interval);
      window.location.href = url;
    } catch (err) {
      setError(serverMessage(err, 'Could not start checkout. Please try again.'));
      setBusy(false);
    }
  }

  return (
    <AccessibleModal
      title={`Subscribe to ${plan.name}`}
      description="Review your plan. You'll complete payment on our secure checkout page."
      maxWidth={460}
      dismissible={!busy}
      onClose={onClose}
    >
      <ModalHeader
        icon="i-ph:arrow-up"
        title={`Subscribe to ${plan.name}`}
        subtitle="Review your plan below. Nothing is charged until you continue."
      />

      <div className="rounded-[10px] border border-hairline bg-raised px-3.5 py-1">
        <Row label="Plan" value={plan.name} />
        <Row label="Billing" value={isAnnual ? 'Annual' : 'Monthly'} />
        {isAnnual && savingsPercent != null && (
          <Row label="Savings" value={<span className="text-success">Save {savingsPercent}%</span>} />
        )}
        <Row
          label="Credits"
          value={
            <span>
              <span className="ui-mono text-accent">{formatCredits(plan.credits)}</span> / month
            </span>
          }
        />
      </div>

      <div className="mt-2.5 flex items-baseline justify-between rounded-[10px] border border-accent/20 bg-accent/[0.04] px-3.5 py-3">
        <span className="text-[13px] font-medium text-primary">Due today</span>
        <span className="ui-serif text-[24px] tracking-[-0.015em] text-accent">
          {price ? formatMoney(price.value, price.currency) : '—'}
          <span className="ml-1 align-baseline text-[12px] text-muted">{isAnnual ? '/year' : '/month'}</span>
        </span>
      </div>

      <div className="mt-3 flex items-start gap-2 rounded-[10px] border border-hairline bg-white/[0.02] px-3.5 py-3 text-[11.5px] leading-[1.55] text-secondary">
        <span className="i-ph:lock-simple mt-px flex-shrink-0 text-[14px] text-accent" />
        <span>
          You'll finish on our secure checkout. Your credits go into the one balance you use on the website, Dunea
          Desktop and Dunea Work as soon as the payment is confirmed.{' '}
          {isAnnual ? 'Billed once a year; credits refill every month. Cancel anytime.' : 'Cancel anytime.'}
        </span>
      </div>

      {!price && <ErrorNote>This price isn't available right now. Please try again shortly.</ErrorNote>}
      {error && <ErrorNote>{error}</ErrorNote>}

      <div className="mt-4.5 flex justify-end gap-2 border-t border-hairline pt-3.5">
        <button type="button" onClick={onClose} disabled={busy} className="ui-btn-ghost">
          Cancel
        </button>
        <button type="button" onClick={continueToPayment} disabled={busy || !price} className="ui-btn-accent">
          {busy && <span className="i-ph:spinner-gap animate-spin text-[13px]" />}
          {busy ? 'Redirecting…' : 'Continue to payment'}
        </button>
      </div>
    </AccessibleModal>
  );
}

function fmtDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}

/**
 * Switch between Plus and Pro, or monthly and annual, on an existing plan. The
 * server previews what it will do (upgrade now with a prorated charge, or a
 * downgrade at the period end); nothing can be confirmed until that preview
 * loads, so the amount shown is always the server's.
 */
export function PlanSwitchModal({
  plan,
  interval,
  app,
  currentName,
  onClose,
  onSwitched,
}: {
  plan: AccountPlanOption;
  interval: PlanInterval;
  app: PlanApp;
  currentName: string;
  onClose: () => void;
  onSwitched: (result: { kind: 'upgraded' | 'scheduled'; effectiveDate: string | null }) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tier = plan.tier as PaidAccountPlanTier;

  const preview = useQuery({
    queryKey: ['account-plan-switch-preview', app, tier, interval],
    queryFn: () => apiPreviewPlanSwitch(tier, interval, app),
    staleTime: 0,
    retry: false,
  });

  async function confirm() {
    setBusy(true);
    setError(null);

    try {
      const result = await apiSwitchPlan(tier, interval, app);
      onSwitched(result);
    } catch (err) {
      setError(serverMessage(err, 'Could not change your plan. Please try again.'));
      setBusy(false);
    }
  }

  const p = preview.data;
  const isUpgrade = p?.kind === 'upgrade';
  const cadence = interval === 'year' ? '/year' : '/month';

  return (
    <AccessibleModal
      title={`Switch to ${plan.name}`}
      description="Review the change before it's made."
      maxWidth={460}
      dismissible={!busy}
      onClose={onClose}
    >
      <ModalHeader
        icon="i-ph:arrows-left-right"
        title={`Switch to ${plan.name}${interval === 'year' ? ' (annual)' : ''}`}
        subtitle={`From ${currentName}. Nothing changes until you confirm.`}
      />

      {preview.isLoading ? (
        <div className="flex flex-col gap-2">
          <div className="h-10 animate-pulse rounded-lg bg-sunken" />
          <div className="h-12 animate-pulse rounded-lg bg-sunken" />
        </div>
      ) : preview.isError || !p ? (
        <div className="rounded-[10px] border border-danger/30 bg-danger/[0.07] px-3.5 py-4 text-center">
          <div className="mb-2 text-[12.5px] text-primary">
            {serverMessage(preview.error, "Couldn't load the price of this change.")}
          </div>
          <button type="button" onClick={() => preview.refetch()} className="ui-btn-secondary">
            Retry
          </button>
        </div>
      ) : (
        <>
          <div className="rounded-[10px] border border-hairline bg-raised px-3.5 py-1">
            <Row label="New plan" value={`${plan.name}, ${interval === 'year' ? 'annual' : 'monthly'}`} />
            <Row label="Price" value={`${formatMoney(p.newPriceMonthly, p.currency)}${cadence}`} />
            <Row label="Credits" value={`${formatCredits(plan.credits)} / month`} />
            {isUpgrade && p.creditsNow > 0 && (
              <Row label="Added now" value={`${formatCredits(p.creditsNow)} credits`} />
            )}
          </div>
          <div className="mt-2.5 flex items-baseline justify-between rounded-[10px] border border-accent/20 bg-accent/[0.04] px-3.5 py-3">
            <span className="text-[13px] font-medium text-primary">Due today</span>
            <span className="ui-serif text-[24px] tracking-[-0.015em] text-accent">
              {formatMoney(p.amountDueToday, p.currency)}
            </span>
          </div>
          <div className="mt-3 text-[11.5px] leading-[1.55] text-secondary">
            {isUpgrade
              ? 'Charged to your card on file now, prorated for the rest of this period. The exact amount is confirmed by our payment provider.'
              : `You keep ${currentName} until ${fmtDate(p.effectiveDate)}, then move to ${plan.name}. Nothing is charged today.`}
          </div>
        </>
      )}

      {error && <ErrorNote>{error}</ErrorNote>}

      <div className="mt-4.5 flex justify-end gap-2 border-t border-hairline pt-3.5">
        <button type="button" onClick={onClose} disabled={busy} className="ui-btn-ghost">
          Cancel
        </button>
        <button type="button" onClick={confirm} disabled={busy || !p} className="ui-btn-accent">
          {busy && <span className="i-ph:spinner-gap animate-spin text-[13px]" />}
          {busy ? 'Working…' : isUpgrade ? 'Confirm and pay' : 'Schedule change'}
        </button>
      </div>
    </AccessibleModal>
  );
}
